import { z } from 'zod';

import { API_PATHS, API_ROUTES } from '../../../lib/apiRoutes';
import { apiRequest } from '../../../lib/api';
import { buildIdempotencyKey } from '../../../lib/idempotency';
import { validateWithSchema } from '../../shared/utils/apiValidation.util';
import type { MappingHistoryEntry, PriceObservation, UnmappedProduct, UnmappedProductCandidate } from '../types/products.types';

const nonEmptyStringSchema = z.string().trim().min(1);
const idempotencyKeySchema = z.string().trim().min(8).max(128);
const resolveIdempotencyKey = (scope: string, provided?: string) => idempotencyKeySchema.parse(provided?.trim() || buildIdempotencyKey(scope));
const upcSchema = z
  .string()
  .trim()
  .transform((value) => value.replace(/\D/g, ''))
  .pipe(z.string().min(8).max(14));

const mapUnmappedPayloadSchema = z.object({
  productId: nonEmptyStringSchema,
  migrateObservations: z.boolean().optional().default(false)
});

const linkUpcPayloadSchema = z.object({
  upc: upcSchema,
  productId: nonEmptyStringSchema
});

const scanUpcPayloadSchema = z.object({
  upc: upcSchema,
  qty: z.number().int().positive(),
  resolveOnly: z.boolean()
});

const upcRegistryMutationSchema = z.object({
  upc: upcSchema,
  name: z.string().trim().min(1).max(200).optional(),
  brand: z.string().trim().max(120).optional(),
  productType: z.string().trim().max(120).optional(),
  containerType: z.enum(['plastic', 'glass', 'aluminum']).optional(),
  isEligible: z.boolean().optional(),
  eligibilityStatus: z.enum(['CANDIDATE', 'VERIFIED_MI', 'NOT_MI_ELIGIBLE', 'NEEDS_REVIEW']).optional(),
  miLabelVerified: z.boolean().optional(),
  verificationMethod: z
    .enum(['admin_manual', 'mi_label_photo', 'store_policy', 'ops_audit', 'legacy_grandfather'])
    .optional(),
  price: z.number().min(0).max(10000).optional(),
  sizeOz: z.number().min(0).max(1024).optional(),
  sizeUnit: z.enum(['oz', 'fl oz', 'g', 'kg', 'ml', 'l']).optional(),
  depositValue: z.number().min(0).max(100).optional()
});

const upcRegistryPatchSchema = upcRegistryMutationSchema.partial().extend({
  upc: upcSchema.optional()
}).refine((payload) => Object.keys(payload).length > 0, {
  message: 'At least one UPC registry field is required'
});


const encodeQuery = (query: URLSearchParams): string => {
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
};

interface ProductMutationResponse {
  ok?: boolean;
  product?: unknown;
  data?: unknown;
  success?: boolean;
  error?: string;
}

interface UpcRegistryResponse {
  ok?: boolean;
  error?: string;
  data?: unknown;
}

interface OpenFoodFactsResponse {
  found?: boolean;
  product?: unknown;
  status?: number;
  code?: string;
  error?: string;
}

interface LinkUpcResponse {
  ok?: boolean;
  error?: string;
  data?: unknown;
}

interface ScanUpcResponse {
  ok?: boolean;
  upc?: string;
  upcEntry?: {
    name?: string;
    price?: number;
    sizeOz?: number;
    containerType?: string;
    isEligible?: boolean;
    depositValue?: number;
  };
  product?: Record<string, unknown>;
  error?: string;
}

interface UpcEligibilityResponse {
  ok?: boolean;
  eligible?: boolean;
  name?: string;
  price?: number;
  sizeOz?: number;
  containerType?: string;
  depositValue?: number;
  reason?: string;
  error?: string;
}

const normalizeProductMutationPayload = (payload: Record<string, unknown>) => {
  const normalized = { ...payload };
  const imageCandidate = normalized.imageUrl ?? normalized.image;
  if (typeof imageCandidate === 'string' && imageCandidate.trim()) {
    normalized.imageUrl = imageCandidate.trim();
    if (typeof normalized.thumbnailUrl !== 'string' || !normalized.thumbnailUrl.trim()) {
      normalized.thumbnailUrl = imageCandidate.trim();
    }
  }
  return normalized;
};

const unwrapApiData = <T>(payload: T | { data?: T }): T => {
  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
};

export const productsApi = {
  getPublicDetails: async (identifier: string) => {
    const id = nonEmptyStringSchema.parse(identifier);
    const payload = await apiRequest<{ data?: unknown }>(API_PATHS.products.byIdentifier(id), { method: 'GET' });
    return unwrapApiData(payload);
  },
  listLive: async (params?: { limit?: number; page?: number; category?: string; search?: string }) => {
    const query = new URLSearchParams();
    query.set('limit', String(params?.limit ?? 2500));
    if (params?.page != null) query.set('page', String(params.page));
    if (params?.category) query.set('category', params.category);
    if (params?.search) query.set('search', params.search);
    const qs = query.toString();
    const endpoint = qs ? `${API_PATHS.products.live}?${qs}` : API_PATHS.products.live;
    const payload = await apiRequest<{ data?: unknown[]; items?: unknown[]; products?: unknown[]; error?: string }>(
      endpoint,
      { method: 'GET' }
    );
    return unwrapApiData(payload);
  },
  temperatureQuote: async (
    productId: string,
    temperaturePreference: 'chilled' | 'ambient' | 'cold' | 'either' = 'ambient'
  ) => {
    const id = nonEmptyStringSchema.parse(productId);
    const query = new URLSearchParams({ temperaturePreference });
    const payload = await apiRequest<{
      data?: {
        productId?: string;
        temperaturePreference?: 'chilled' | 'ambient' | 'cold' | 'either';
        available?: boolean;
        coldUnavailable?: boolean;
        chilledUnavailable?: boolean;
        unitPrice?: number | null;
        unitPriceCents?: number | null;
        storeId?: string | null;
        storeName?: string | null;
        temperatureClass?: string;
      };
    }>(`${API_PATHS.products.temperatureQuote(id)}?${query.toString()}`, { method: 'GET' });
    return unwrapApiData(payload);
  },
  create: (payload: Record<string, unknown>, idempotencyKey?: string) =>
    apiRequest<ProductMutationResponse>(API_ROUTES.v1.products, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('product-create', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(normalizeProductMutationPayload(payload))
    }),
  update: (id: string, payload: Record<string, unknown>, idempotencyKey?: string) =>
    apiRequest<ProductMutationResponse>(`${API_ROUTES.v1.products}/${nonEmptyStringSchema.parse(id)}`, {
      method: 'PATCH',
      idempotencyKey: resolveIdempotencyKey('product-update', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(normalizeProductMutationPayload(payload))
    }),
  remove: (id: string) =>
    apiRequest<{ ok?: boolean; error?: string }>(`${API_ROUTES.v1.products}/${nonEmptyStringSchema.parse(id)}`, {
      method: 'DELETE',
      idempotencyKey: resolveIdempotencyKey('product-delete'),
      requireIdempotencyKey: true
    }),
  listUnmapped: (query: URLSearchParams) =>
    apiRequest<{ items: UnmappedProduct[]; error?: string }>(`${API_PATHS.unmappedProducts.root}${encodeQuery(query)}`),
  getUnmappedHistory: (id: string) =>
    apiRequest<{ items: MappingHistoryEntry[]; error?: string }>(API_PATHS.unmappedProducts.history(nonEmptyStringSchema.parse(id))),
  getPriceObservations: (query: URLSearchParams) =>
    apiRequest<{ items: PriceObservation[]; error?: string }>(`${API_PATHS.priceIntel.observations}${encodeQuery(query)}`),
  mapUnmapped: (id: string, payload: { productId: string; migrateObservations?: boolean }, idempotencyKey?: string) => {
    const endpoint = API_PATHS.unmappedProducts.map(nonEmptyStringSchema.parse(id));
    const normalizedPayload = validateWithSchema<z.infer<typeof mapUnmappedPayloadSchema>>(mapUnmappedPayloadSchema, payload, endpoint);
    return apiRequest<{ error?: string }>(endpoint, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('unmapped-map', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify({
        productId: normalizedPayload.productId.trim(),
        migrateObservations: Boolean(normalizedPayload.migrateObservations)
      })
    });
  },
  ignoreUnmapped: (id: string, idempotencyKey?: string) =>
    apiRequest<{ error?: string }>(API_PATHS.unmappedProducts.ignore(nonEmptyStringSchema.parse(id)), {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('unmapped-ignore', idempotencyKey),
      requireIdempotencyKey: true
    }),
  getRegistryPreview: (id: string) =>
    apiRequest<{
      success: boolean;
      data: {
        name: string;
        brand: string;
        category: string;
        price: number;
        sku: string;
        upc: string;
        containerType: 'glass' | 'plastic' | 'aluminum' | 'other';
        isEligible: boolean;
        sizeOz: number;
        description: string;
        source: string;
      };
    }>(API_PATHS.unmappedProducts.registryPreview(nonEmptyStringSchema.parse(id))),
  promoteFromRegistry: (id: string, payload?: Record<string, unknown>, idempotencyKey?: string) =>
    apiRequest<{ success: boolean; data?: { productId: string }; error?: string }>(
      API_PATHS.unmappedProducts.promote(nonEmptyStringSchema.parse(id)),
      {
        method: 'POST',
        idempotencyKey: resolveIdempotencyKey('unmapped-promote', idempotencyKey),
        requireIdempotencyKey: true,
        body: payload ? JSON.stringify(payload) : undefined
      }
    ),
  createUnmappedCandidate: (payload: { storeId: string; rawName: string; normalizedName?: string }, idempotencyKey?: string) =>
    apiRequest<{ ok: boolean; item?: UnmappedProductCandidate; error?: string }>(API_PATHS.unmappedProducts.root, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('unmapped-create', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(payload)
    }),
  bulkIngestCandidates: (payload: { storeId: string; images: string[] }, idempotencyKey?: string) =>
    apiRequest<{ ok: boolean; items?: UnmappedProductCandidate[]; failedImages?: number; error?: string }>(`${API_PATHS.unmappedProducts.root}/bulkIngestion`, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('unmapped-bulk-ingestion', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(payload)
    }),
  addUpcRegistry: (payload: Record<string, unknown>, idempotencyKey?: string) =>
    apiRequest<UpcRegistryResponse>(API_PATHS.upc.root, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('upc-registry-add', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema<z.infer<typeof upcRegistryMutationSchema>>(upcRegistryMutationSchema, payload, API_PATHS.upc.root))
    }),

  fetchOpenFoodFacts: (upc: string) =>
    apiRequest<OpenFoodFactsResponse>(API_PATHS.upc.off(validateWithSchema(upcSchema, upc, API_PATHS.upc.off(':upc')))),
  linkUpc: (payload: { upc: string; productId: string }, idempotencyKey?: string) =>
    apiRequest<LinkUpcResponse>(API_PATHS.upc.link, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('upc-link', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema<z.infer<typeof linkUpcPayloadSchema>>(linkUpcPayloadSchema, payload, API_PATHS.upc.link))
    }),
  scanUpc: (payload: { upc: string; qty: number; resolveOnly: boolean }, idempotencyKey?: string) =>
    apiRequest<ScanUpcResponse>(API_PATHS.upc.scan, {
      method: 'POST',
      idempotencyKey: resolveIdempotencyKey('upc-scan', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema<z.infer<typeof scanUpcPayloadSchema>>(scanUpcPayloadSchema, payload, API_PATHS.upc.scan))
    }),
  getUpcEligibility: (upc: string) =>
    apiRequest<UpcEligibilityResponse>(API_PATHS.upc.eligibility(validateWithSchema(upcSchema, upc, API_PATHS.upc.eligibility(':upc')))),
  patchUpcRegistry: (upc: string, payload: Record<string, unknown>, idempotencyKey?: string) =>
    apiRequest<UpcRegistryResponse>(`${API_PATHS.upc.root}/${validateWithSchema(upcSchema, upc, `${API_PATHS.upc.root}/:upc`)}`, {
      method: 'PATCH',
      idempotencyKey: resolveIdempotencyKey('upc-registry-patch', idempotencyKey),
      requireIdempotencyKey: true,
      body: JSON.stringify(validateWithSchema<z.infer<typeof upcRegistryPatchSchema>>(upcRegistryPatchSchema, payload, `${API_PATHS.upc.root}/:upc`))
    })
};
